import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useNavigate, useParams, Outlet, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';
import {
  LayoutDashboard, Users as UsersIcon, Globe, Mail, AlertTriangle, CheckCircle,
  ArrowUpRight, Plus, Search, Settings, LogOut, Server, Shield, Inbox, MessageSquare,
  ShieldCheck, UserCheck, UserX, Trash2, Clock, RefreshCw, Menu, X,
  Copy, ExternalLink, XCircle, AlertCircle, Info, Download, Paperclip, AtSign,
  ChevronDown, ChevronRight
} from 'lucide-react';
import './App.css';

const GOOGLE_CLIENT_ID = '141913349498-ns8k00vncogsvll47tlblipsq9hittv6.apps.googleusercontent.com';

const API_URL = '/api';

// Configurar axios
const api = axios.create({ baseURL: API_URL });

// Decodifica payload do JWT (base64url) e checa expiração; true se expirado/inválido
function isTokenExpired(token) {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    if (!payload.exp) return false;
    return payload.exp * 1000 < Date.now();
  } catch (e) {
    return true;
  }
}

function clearAuthStorage() {
  localStorage.removeItem('token');
  localStorage.removeItem('username');
  localStorage.removeItem('userName');
  localStorage.removeItem('userPicture');
  localStorage.removeItem('userRole');
}

// Na inicialização: se tinha token mas já expirou, limpa antes do app montar
if (localStorage.getItem('token') && isTokenExpired(localStorage.getItem('token'))) {
  clearAuthStorage();
}

api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Se a API devolver 401 numa request autenticada, força logout (token expirou server-side)
let authExpiredHandled = false;
api.interceptors.response.use(
  res => res,
  err => {
    const hadToken = !!err.config?.headers?.Authorization;
    if (err.response?.status === 401 && hadToken && !authExpiredHandled) {
      authExpiredHandled = true;
      clearAuthStorage();
      // reload traz de volta para /manager/ e o App() vai montar Login (token sumiu)
      window.location.reload();
    }
    return Promise.reject(err);
  }
);

// ==================== COMPONENTES ====================

// Login com Google OAuth
function Login({ onLogin }) {
  const [error, setError] = useState('');
  const [pending, setPending] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true);
    setError('');
    setPending(null);

    try {
      const response = await api.post('/auth/google', {
        credential: credentialResponse.credential,
      });
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('username', response.data.email);
      localStorage.setItem('userName', response.data.name);
      localStorage.setItem('userPicture', response.data.picture || '');
      localStorage.setItem('userRole', response.data.role);
      onLogin(response.data);
    } catch (err) {
      if (err.response?.status === 403) {
        setPending({
          name: err.response.data.name,
          email: err.response.data.email,
          status: err.response.data.status,
        });
      } else {
        setError(err.response?.data?.error || 'Erro ao fazer login');
      }
    } finally {
      setLoading(false);
    }
  };

  // Tela de acesso pendente
  if (pending) {
    return (
      <div className="login-container">
        <div className="login-box pending-box">
          <div className="pending-icon">
            <Clock size={48} />
          </div>
          <h1>Acesso Pendente</h1>
          <p className="pending-name">{pending.name}</p>
          <p className="pending-email">{pending.email}</p>
          <div className="pending-message">
            {pending.status === 'frozen'
              ? 'Seu acesso foi congelado pelo administrador.'
              : 'Seu acesso está sendo analisado pelo administrador. Tente novamente mais tarde.'}
          </div>
          <div className="pending-actions">
            <button onClick={() => setPending(null)} className="btn-secondary">
              <RefreshCw size={16} /> Tentar novamente
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-box">
        <h1>iRedMail Manager</h1>
        <p>Gerenciamento de Email</p>
        {error && <div className="error">{error}</div>}
        {loading && <div className="login-loading">Autenticando...</div>}
        <div className="google-login-wrapper">
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={() => setError('Erro ao autenticar com Google')}
            size="large"
            width="320"
            text="signin_with"
            shape="rectangular"
            theme="outline"
          />
        </div>
      </div>
    </div>
  );
}

// Gestão de Acesso (superadmin)
function AccessManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/manager-users');
      setUsers(response.data);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadUsers(); }, []);

  const handleApprove = async (id) => {
    try {
      await api.put(`/manager-users/${id}/approve`);
      loadUsers();
    } catch (error) {
      console.error('Erro ao aprovar:', error);
    }
  };

  const handleFreeze = async (id) => {
    try {
      await api.put(`/manager-users/${id}/freeze`);
      loadUsers();
    } catch (error) {
      console.error('Erro ao congelar:', error);
    }
  };

  const handleRemove = async (id) => {
    if (!window.confirm('Tem certeza que deseja remover este usuário?')) return;
    try {
      await api.delete(`/manager-users/${id}`);
      loadUsers();
    } catch (error) {
      console.error('Erro ao remover:', error);
    }
  };

  const getStatusBadge = (status) => {
    const labels = { approved: 'Aprovado', pending: 'Pendente', frozen: 'Congelado' };
    const cls = `badge badge-${status || 'pending'}`;
    return <span className={cls}>{labels[status] || labels.pending}</span>;
  };

  const getRoleBadge = (role) => {
    const labels = { superadmin: 'Super Admin', admin: 'Admin', viewer: 'Viewer' };
    const cls = `badge badge-${role || 'viewer'}`;
    return <span className={cls}>{labels[role] || labels.viewer}</span>;
  };

  if (loading) return <div className="loading">Carregando...</div>;

  return (
    <div className="page-content">
      <div className="page-header">
        <h1><ShieldCheck size={24} /> Gestão de Acesso</h1>
        <p>Gerenciar usuários do Manager</p>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Usuário</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Criado em</th>
              <th>Último login</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id}>
                <td>
                  <div className="avatar">
                    {user.picture ? (
                      <img src={user.picture} alt="" />
                    ) : (
                      <div className="avatar-fallback">
                        {(user.name || user.email)[0].toUpperCase()}
                      </div>
                    )}
                    <span>{user.name || '-'}</span>
                  </div>
                </td>
                <td>{user.email}</td>
                <td>{getRoleBadge(user.role)}</td>
                <td>{getStatusBadge(user.status)}</td>
                <td>{user.created_at ? new Date(user.created_at).toLocaleDateString('pt-BR') : '-'}</td>
                <td>{user.last_login ? new Date(user.last_login).toLocaleString('pt-BR') : 'Nunca'}</td>
                <td>
                  {user.role !== 'superadmin' && (
                    <div className="actions-row">
                      {user.status !== 'approved' && (
                        <button className="btn-icon btn-approve" onClick={() => handleApprove(user.id)} title="Aprovar">
                          <UserCheck size={16} />
                        </button>
                      )}
                      {user.status === 'approved' && (
                        <button className="btn-icon btn-freeze" onClick={() => handleFreeze(user.id)} title="Congelar">
                          <UserX size={16} />
                        </button>
                      )}
                      <button className="btn-icon btn-remove" onClick={() => handleRemove(user.id)} title="Remover">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Sidebar
function Sidebar({ onLogout, isOpen, onClose }) {
  const navigate = useNavigate();
  const userRole = localStorage.getItem('userRole');
  const userName = localStorage.getItem('userName');
  const userPicture = localStorage.getItem('userPicture');

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    localStorage.removeItem('userName');
    localStorage.removeItem('userPicture');
    localStorage.removeItem('userRole');
    onLogout();
    navigate('/login');
  };

  const handleNavClick = () => {
    if (onClose) onClose();
  };

  return (
    <div className={`sidebar ${isOpen ? 'open' : ''}`}>
      <div className="sidebar-header">
        <h2>iRedMail</h2>
        <span>Manager</span>
      </div>
      <nav>
        <Link to="/dashboard" onClick={handleNavClick}>
          <LayoutDashboard size={20} /> Dashboard
        </Link>
        <Link to="/messages" onClick={handleNavClick}>
          <Inbox size={20} /> Mensagens
        </Link>
        <Link to="/domains" onClick={handleNavClick}>
          <Globe size={20} /> Domínios
        </Link>
        <Link to="/users" onClick={handleNavClick}>
          <UsersIcon size={20} /> Usuários
        </Link>
        <Link to="/aliases" onClick={handleNavClick}>
          <Mail size={20} /> Aliases
        </Link>
        <Link to="/catchall" onClick={handleNavClick}>
          <AtSign size={20} /> Catch-all
        </Link>
        <Link to="/config" onClick={handleNavClick}>
          <Settings size={20} /> Configurações
        </Link>
        {userRole === 'superadmin' && (
          <Link to="/access" onClick={handleNavClick}>
            <ShieldCheck size={20} /> Gestão de Acesso
          </Link>
        )}
      </nav>
      <div className="sidebar-footer">
        <div className="sidebar-user">
          {userPicture && <img src={userPicture} alt="" className="sidebar-avatar" />}
          <small>{userName || localStorage.getItem('username')}</small>
        </div>
        <button onClick={handleLogout}>
          <LogOut size={16} /> Sair
        </button>
      </div>
    </div>
  );
}

// Dashboard
function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await api.get('/stats');
      setStats(response.data);
    } catch (err) {
      console.error('Erro ao carregar estatísticas:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="loading">Carregando dashboard...</div>;

  const quotaData = [
    { name: 'Saudável (<70%)', value: stats?.quotaStats?.safe || 0, color: '#059669' },
    { name: 'Atenção (70-90%)', value: stats?.quotaStats?.warning || 0, color: '#d97706' },
    { name: 'Crítico (>90%)', value: stats?.quotaStats?.critical || 0, color: '#dc2626' },
  ].filter(d => d.value > 0);

  return (
    <div className="content dashboard-content">
      <div className="dashboard-header">
        <div>
          <h1>Visão Geral</h1>
          <p className="subtitle">Bem-vindo ao painel de controle do iRedMail</p>
        </div>
        <div className="quick-actions">
           <button className="btn-primary" onClick={() => navigate('/users?new=true')}>
             <Plus size={16} /> Novo Usuário
           </button>
           <button className="btn-secondary" onClick={() => navigate('/domains?new=true')}>
             <Plus size={16} /> Novo Domínio
           </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon icon-blue">
            <Globe size={24} />
          </div>
          <div className="stat-info">
            <h3>{stats?.totalDomains || 0}</h3>
            <p>Domínios</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon icon-purple">
            <UsersIcon size={24} />
          </div>
          <div className="stat-info">
            <h3>{stats?.totalUsers || 0}</h3>
            <p>Usuários Totais</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon icon-green">
            <CheckCircle size={24} />
          </div>
          <div className="stat-info">
            <h3>{stats?.activeUsers || 0}</h3>
            <p>Usuários Ativos</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon icon-orange">
            <Mail size={24} />
          </div>
          <div className="stat-info">
            <h3>{stats?.totalAliases || 0}</h3>
            <p>Aliases</p>
          </div>
        </div>
      </div>

      <div className="charts-row">
        {/* Users per Domain Chart */}
        <div className="chart-card">
          <h3>Usuários por Domínio</h3>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stats?.usersPerDomain || []}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="domain" hide />
                <YAxis />
                <RechartsTooltip
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.07)' }}
                />
                <Bar dataKey="count" name="Usuários" fill="#2563eb" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Quota Health Chart */}
        <div className="chart-card">
          <h3>Saúde do Armazenamento (Quota)</h3>
          <div className="chart-container">
            {quotaData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={quotaData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {quotaData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                  <Legend verticalAlign="bottom" height={36}/>
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="empty-chart">Sem dados de quota</div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="recent-activity">
        <div className="section-header">
          <h3>Usuários Recentes</h3>
          <Link to="/users" className="btn-link">Ver todos <ArrowUpRight size={16} /></Link>
        </div>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Usuário</th>
                <th>Domínio</th>
                <th>Data Criação</th>
              </tr>
            </thead>
            <tbody>
              {stats?.recentUsers?.map(u => (
                <tr key={u.username}>
                  <td><strong>{u.username.split('@')[0]}</strong></td>
                  <td>{u.domain || u.username.split('@')[1]}</td>
                  <td>{new Date(u.created).toLocaleDateString('pt-BR')}</td>
                </tr>
              ))}
              {(!stats?.recentUsers || stats.recentUsers.length === 0) && (
                <tr><td colSpan="3" className="empty-message">Nenhum usuário recente</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Domains
function Domains() {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ domain: '', description: '' });

  useEffect(() => {
    loadDomains();
  }, []);

  const loadDomains = async () => {
    try {
      const response = await api.get('/domains');
      setDomains(response.data);
    } catch (err) {
      console.error('Erro ao carregar domínios:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/domains', formData);
      setShowModal(false);
      setFormData({ domain: '', description: '' });
      loadDomains();
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao criar domínio');
    }
  };

  const handleDelete = async (domain) => {
    if (!window.confirm(`Deseja realmente excluir o domínio ${domain}?`)) return;
    try {
      await api.delete(`/domains/${domain}`);
      loadDomains();
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao excluir domínio');
    }
  };

  const toggleActive = async (domain, currentActive) => {
    try {
      await api.put(`/domains/${domain}`, { ...domain, active: !currentActive });
      loadDomains();
    } catch (err) {
      alert('Erro ao atualizar domínio');
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  if (loading) return <div className="loading">Carregando...</div>;

  return (
    <div className="content">
      <div className="content-header">
        <h1>Dominios</h1>
        <button className="btn-primary" onClick={() => setShowModal(true)}>
          + Novo Dominio
        </button>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Dominio</th>
              <th>Descricao</th>
              <th>Mailboxes</th>
              <th>Armazenamento (Usado / Total)</th>
              <th>Status</th>
              <th>Criado</th>
              <th>Acoes</th>
            </tr>
          </thead>
          <tbody>
            {domains.map(d => {
               const allocatedBytes = (parseInt(d.total_allocated_mb) || 0) * 1024 * 1024;
               const usedBytes = parseInt(d.total_used_bytes) || 0;
               const percentage = allocatedBytes > 0 ? Math.round((usedBytes / allocatedBytes) * 100) : 0;

               return (
                <tr key={d.domain}>
                  <td>
                    <Link to={`/domains/${d.domain}`} className="domain-link">
                      <strong>{d.domain}</strong>
                    </Link>
                  </td>
                  <td>{d.description || '-'}</td>
                  <td>{d.mailboxes || 0}</td>
                  <td>
                    <div className="storage-bar-wrapper">
                       <div className="storage-bar-labels">
                          <span>{formatBytes(usedBytes)}</span>
                          <span>{formatBytes(allocatedBytes)}</span>
                       </div>
                       <div className="storage-bar-track">
                          <div className={`storage-bar-fill ${percentage > 90 ? 'storage-bar-danger' : percentage > 70 ? 'storage-bar-warning' : 'storage-bar-safe'}`} style={{
                             width: `${Math.min(percentage, 100)}%`
                          }}></div>
                       </div>
                       <div className="storage-bar-percent">
                          {percentage}% utilizado
                       </div>
                    </div>
                  </td>
                  <td>
                    <span className={`status ${d.active ? 'active' : 'inactive'}`}
                          onClick={() => toggleActive(d.domain, d.active)}>
                      {d.active ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td>{d.created}</td>
                  <td>
                    <Link to={`/domains/${d.domain}`} className="btn-small">
                      Detalhes
                    </Link>
                    <Link to={`/domains/${d.domain}/dns`} className="btn-small">
                      DNS
                    </Link>
                    <button className="btn-danger btn-small" onClick={() => handleDelete(d.domain)}>
                      Excluir
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Novo Dominio</h2>
            <form onSubmit={handleCreate}>
              <input
                type="text"
                placeholder="exemplo.com.br"
                value={formData.domain}
                onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                required
              />
              <input
                type="text"
                placeholder="Descricao (opcional)"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
              <div className="modal-actions">
                <button type="button" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="btn-primary">Criar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// Users
function Users() {
  const [users, setUsers] = useState([]);
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [filterDomain, setFilterDomain] = useState('');
  const [formData, setFormData] = useState({
    username: '', password: '', name: '', domain: '', quota: 1024
  });
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usersRes, domainsRes] = await Promise.all([
        api.get('/users'),
        api.get('/domains')
      ]);
      setUsers(usersRes.data);
      setDomains(domainsRes.data);
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/users', formData);
      setShowModal(false);
      setFormData({ username: '', password: '', name: '', domain: '', quota: 1024 });
      loadData();
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao criar usuário');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/users/${selectedUser}/password`, { password: newPassword });
      setShowPasswordModal(false);
      setNewPassword('');
      setSelectedUser(null);
      alert('Senha alterada com sucesso!');
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao alterar senha');
    }
  };

  const handleDelete = async (username) => {
    if (!window.confirm(`Deseja realmente excluir ${username}?`)) return;
    try {
      await api.delete(`/users/${username}`);
      loadData();
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao excluir usuário');
    }
  };

  const toggleActive = async (user) => {
    try {
      await api.put(`/users/${user.username}`, {
        ...user,
        active: !user.active,
        quota: user.quota
      });
      loadData();
    } catch (err) {
      alert('Erro ao atualizar usuário');
    }
  };

  const filteredUsers = filterDomain
    ? users.filter(u => u.domain === filterDomain)
    : users;

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) return <div className="loading">Carregando...</div>;

  return (
    <div className="content">
      <div className="content-header">
        <h1>Usuarios ({filteredUsers.length})</h1>
        <div className="header-actions">
          <select value={filterDomain} onChange={(e) => setFilterDomain(e.target.value)}>
            <option value="">Todos os dominios</option>
            {domains.map(d => (
              <option key={d.domain} value={d.domain}>{d.domain}</option>
            ))}
          </select>
          <button className="btn-primary" onClick={() => setShowModal(true)}>
            + Novo Usuario
          </button>
        </div>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Email</th>
              <th>Nome</th>
              <th>Quota (MB)</th>
              <th>Uso</th>
              <th>Admin</th>
              <th>Status</th>
              <th>Senha Alterada</th>
              <th>Acoes</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(u => {
              const percentage = u.quota > 0 
                ? Math.round((u.used_quota / (u.quota * 1024 * 1024)) * 100) 
                : 0;
              
              return (
                <tr key={u.username}>
                  <td>
                    <Link to={`/users/${encodeURIComponent(u.username)}`} className="domain-link">
                      <strong>{u.username}</strong>
                    </Link>
                  </td>
                  <td>{u.name || '-'}</td>
                  <td>{Math.round(u.quota)}</td>
                  <td>
                    <div className="quota-cell">
                       <span>{formatBytes(u.used_quota)}</span>
                       <span className={percentage > 90 ? 'quota-danger' : percentage > 70 ? 'quota-warning' : 'quota-safe'}>
                         {percentage}%
                       </span>
                    </div>
                  </td>
                  <td>{u.isglobaladmin ? 'Global' : u.isadmin ? 'Sim' : '-'}</td>
                  <td>
                    <span className={`status ${u.active ? 'active' : 'inactive'}`}
                          onClick={() => toggleActive(u)}>
                      {u.active ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td>{u.passwordlastchange || '-'}</td>
                  <td>
                    <Link to={`/users/${encodeURIComponent(u.username)}`} className="btn-small">
                      Detalhes
                    </Link>
                    <button
                      className="btn-small"
                      onClick={() => { setSelectedUser(u.username); setShowPasswordModal(true); }}
                    >
                      Senha
                    </button>
                    <button className="btn-danger btn-small" onClick={() => handleDelete(u.username)}>
                      Excluir
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Novo Usuario</h2>
            <form onSubmit={handleCreate}>
              <select
                value={formData.domain}
                onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                required
              >
                <option value="">Selecione o dominio</option>
                {domains.map(d => (
                  <option key={d.domain} value={d.domain}>{d.domain}</option>
                ))}
              </select>
              <input
                type="text"
                placeholder="usuario (sem @dominio)"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                required
              />
              <input
                type="password"
                placeholder="Senha"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
              />
              <input
                type="text"
                placeholder="Nome completo"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
              <input
                type="number"
                placeholder="Quota (MB)"
                value={formData.quota}
                onChange={(e) => setFormData({ ...formData, quota: parseInt(e.target.value) })}
              />
              <div className="modal-actions">
                <button type="button" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="btn-primary">Criar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showPasswordModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Alterar Senha</h2>
            <p>Usuario: <strong>{selectedUser}</strong></p>
            <form onSubmit={handleChangePassword}>
              <input
                type="password"
                placeholder="Nova senha (min. 6 caracteres)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
              />
              <div className="modal-actions">
                <button type="button" onClick={() => { setShowPasswordModal(false); setNewPassword(''); }}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">Alterar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// Domain Details
function DomainDetails() {
  const { domain } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showAliasModal, setShowAliasModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [userForm, setUserForm] = useState({ username: '', password: '', name: '', quota: 1024 });
  const [aliasForm, setAliasForm] = useState({ address: '', forwarding: '' });

  useEffect(() => {
    loadDomain();
  }, [domain]);

  const loadDomain = async () => {
    try {
      const response = await api.get(`/domains/${domain}`);
      setData(response.data);
    } catch (err) {
      console.error('Erro ao carregar domínio:', err);
      if (err.response?.status === 404) {
        navigate('/domains');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/users/${selectedUser}/password`, { password: newPassword });
      setShowPasswordModal(false);
      setNewPassword('');
      setSelectedUser(null);
      alert('Senha alterada com sucesso!');
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao alterar senha');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await api.post('/users', { ...userForm, domain });
      setShowUserModal(false);
      setUserForm({ username: '', password: '', name: '', quota: 1024 });
      loadDomain();
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao criar usuário');
    }
  };

  const handleCreateAlias = async (e) => {
    e.preventDefault();
    try {
      await api.post('/aliases', { ...aliasForm, domain });
      setShowAliasModal(false);
      setAliasForm({ address: '', forwarding: '' });
      loadDomain();
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao criar alias');
    }
  };

  const handleDeleteUser = async (username) => {
    if (!window.confirm(`Deseja realmente excluir ${username}?`)) return;
    try {
      await api.delete(`/users/${username}`);
      loadDomain();
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao excluir usuário');
    }
  };

  const handleDeleteAlias = async (address) => {
    if (!window.confirm(`Deseja realmente excluir ${address}?`)) return;
    try {
      await api.delete(`/aliases/${encodeURIComponent(address)}`);
      loadDomain();
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao excluir alias');
    }
  };

  const toggleUserActive = async (user) => {
    try {
      await api.put(`/users/${user.username}`, {
        ...user,
        active: !user.active,
        quota: user.quota
      });
      loadDomain();
    } catch (err) {
      alert('Erro ao atualizar usuário');
    }
  };

  if (loading) return <div className="loading">Carregando...</div>;
  if (!data) return <div className="content"><p>Domínio não encontrado</p></div>;

  return (
    <div className="content">
      <div className="content-header">
        <div>
          <Link to="/domains" className="back-link">← Voltar para Domínios</Link>
          <h1>{data.domain}</h1>
          <p className="domain-description">{data.description || 'Sem descrição'}</p>
        </div>
        <div className="header-actions">
          <Link to={`/domains/${data.domain}/dns`} className="btn-secondary">
            <ShieldCheck size={16} /> Ferramentas DNS
          </Link>
          <span className={`status ${data.active ? 'active' : 'inactive'}`}>
            {data.active ? 'Ativo' : 'Inativo'}
          </span>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <h3>{data.stats.totalUsers}</h3>
          <p>Usuários</p>
        </div>
        <div className="stat-card">
          <h3>{data.stats.activeUsers}</h3>
          <p>Ativos</p>
        </div>
        <div className="stat-card">
          <h3>{data.stats.totalAliases}</h3>
          <p>Aliases</p>
        </div>
        <div className="stat-card">
          <h3>{data.stats.totalQuotaMB} MB</h3>
          <p>Quota Total</p>
        </div>
      </div>

      {/* Domain Info */}
      <div className="info-section">
        <h3>Informações do Domínio</h3>
        <div className="info-grid">
          <div><strong>Transport:</strong> {data.transport}</div>
          <div><strong>Criado em:</strong> {data.created}</div>
          <div><strong>Modificado em:</strong> {data.modified}</div>
          <div><strong>Administradores:</strong> {data.stats.admins}</div>
        </div>
      </div>

      {/* Users Section */}
      <div className="section">
        <div className="section-header">
          <h2>Usuários ({data.users.length})</h2>
          <button className="btn-primary" onClick={() => setShowUserModal(true)}>
            + Novo Usuário
          </button>
        </div>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>Nome</th>
                <th>Quota (MB)</th>
                <th>Admin</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {data.users.map(u => (
                <tr key={u.username}>
                  <td>
                    <Link to={`/users/${encodeURIComponent(u.username)}`} className="domain-link">
                      <strong>{u.username}</strong>
                    </Link>
                  </td>
                  <td>{u.name || '-'}</td>
                  <td>{Math.round(u.quota)}</td>
                  <td>{u.isglobaladmin ? 'Global' : u.isadmin ? 'Sim' : '-'}</td>
                  <td>
                    <span className={`status ${u.active ? 'active' : 'inactive'}`}
                          onClick={() => toggleUserActive(u)}>
                      {u.active ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td>
                    <Link to={`/users/${encodeURIComponent(u.username)}`} className="btn-small">
                      Detalhes
                    </Link>
                    <button className="btn-small"
                            onClick={() => { setSelectedUser(u.username); setShowPasswordModal(true); }}>
                      Senha
                    </button>
                    <button className="btn-danger btn-small" onClick={() => handleDeleteUser(u.username)}>
                      Excluir
                    </button>
                  </td>
                </tr>
              ))}
              {data.users.length === 0 && (
                <tr><td colSpan="6" className="empty-message">Nenhum usuário neste domínio</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Aliases Section */}
      <div className="section">
        <div className="section-header">
          <h2>Aliases ({data.aliases.length})</h2>
          <button className="btn-primary" onClick={() => setShowAliasModal(true)}>
            + Novo Alias
          </button>
        </div>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Alias</th>
                <th>Encaminha Para</th>
                <th>Criado</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {data.aliases.map((a, idx) => (
                <tr key={idx}>
                  <td><strong>{a.address}</strong></td>
                  <td>{a.forwarding}</td>
                  <td>{a.created}</td>
                  <td>
                    <button className="btn-danger btn-small" onClick={() => handleDeleteAlias(a.address)}>
                      Excluir
                    </button>
                  </td>
                </tr>
              ))}
              {data.aliases.length === 0 && (
                <tr><td colSpan="4" className="empty-message">Nenhum alias neste domínio</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Password Modal */}
      {showPasswordModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Alterar Senha</h2>
            <p>Usuário: <strong>{selectedUser}</strong></p>
            <form onSubmit={handleChangePassword}>
              <input
                type="password"
                placeholder="Nova senha (min. 6 caracteres)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
              />
              <div className="modal-actions">
                <button type="button" onClick={() => { setShowPasswordModal(false); setNewPassword(''); }}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">Alterar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* User Modal */}
      {showUserModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Novo Usuário em {domain}</h2>
            <form onSubmit={handleCreateUser}>
              <input
                type="text"
                placeholder="usuario (sem @dominio)"
                value={userForm.username}
                onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
                required
              />
              <input
                type="password"
                placeholder="Senha"
                value={userForm.password}
                onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                required
              />
              <input
                type="text"
                placeholder="Nome completo"
                value={userForm.name}
                onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
              />
              <input
                type="number"
                placeholder="Quota (MB)"
                value={userForm.quota}
                onChange={(e) => setUserForm({ ...userForm, quota: parseInt(e.target.value) })}
              />
              <div className="modal-actions">
                <button type="button" onClick={() => setShowUserModal(false)}>Cancelar</button>
                <button type="submit" className="btn-primary">Criar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Alias Modal */}
      {showAliasModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Novo Alias em {domain}</h2>
            <form onSubmit={handleCreateAlias}>
              <input
                type="text"
                placeholder="alias (sem @dominio)"
                value={aliasForm.address}
                onChange={(e) => setAliasForm({ ...aliasForm, address: e.target.value })}
                required
              />
              <input
                type="email"
                placeholder="Encaminhar para (email completo)"
                value={aliasForm.forwarding}
                onChange={(e) => setAliasForm({ ...aliasForm, forwarding: e.target.value })}
                required
              />
              <div className="modal-actions">
                <button type="button" onClick={() => setShowAliasModal(false)}>Cancelar</button>
                <button type="submit" className="btn-primary">Criar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// User Details
function UserDetails() {
  const { username } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [emails, setEmails] = useState([]);
  const [inbox, setInbox] = useState([]); // Nova state para inbox real
  const [inboxPage, setInboxPage] = useState(1);
  const [inboxTotalPages, setInboxTotalPages] = useState(1);
  const [inboxSearch, setInboxSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingEmails, setLoadingEmails] = useState(true);
  const [loadingInbox, setLoadingInbox] = useState(true);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [activeTab, setActiveTab] = useState('info');

  useEffect(() => {
    loadUser();
    loadEmails();
    loadInbox(1, '');
  }, [username]);

  const loadUser = async () => {
    try {
      const response = await api.get(`/users/${encodeURIComponent(username)}/details`);
      setUser(response.data);
    } catch (err) {
      console.error('Erro ao carregar usuário:', err);
      if (err.response?.status === 404) {
        navigate('/users');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadEmails = async () => {
    try {
      const response = await api.get(`/users/${encodeURIComponent(username)}/emails`);
      setEmails(response.data);
    } catch (err) {
      console.error('Erro ao carregar logs de email:', err);
    } finally {
      setLoadingEmails(false);
    }
  };

  const loadInbox = async (page = 1, search = '') => {
    setLoadingInbox(true);
    try {
      const response = await api.get(`/users/${encodeURIComponent(username)}/inbox`, {
        params: { page, q: search, limit: 20 }
      });
      setInbox(response.data.data);
      setInboxTotalPages(response.data.meta.totalPages);
      setInboxPage(response.data.meta.page);
    } catch (err) {
      console.error('Erro ao carregar inbox:', err);
    } finally {
      setLoadingInbox(false);
    }
  };

  const handleSearchInbox = (e) => {
    e.preventDefault();
    setInboxPage(1);
    loadInbox(1, inboxSearch);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= inboxTotalPages) {
      loadInbox(newPage, inboxSearch);
    }
  };

  const handleViewEmail = (filename) => {
    window.open(`/manager/email/${encodeURIComponent(username)}/${encodeURIComponent(filename)}`, '_blank');
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/users/${encodeURIComponent(username)}/password`, { password: newPassword });
      setShowPasswordModal(false);
      setNewPassword('');
      alert('Senha alterada com sucesso!');
      loadUser();
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao alterar senha');
    }
  };

  const toggleActive = async () => {
    try {
      await api.put(`/users/${encodeURIComponent(username)}`, {
        name: user.name,
        active: !user.active,
        quota: user.quotaMB,
        isglobaladmin: user.isglobaladmin,
        isadmin: user.isadmin
      });
      loadUser();
    } catch (err) {
      alert('Erro ao atualizar usuário');
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) return <div className="loading">Carregando...</div>;
  if (!user) return <div className="content"><p>Usuário não encontrado</p></div>;

  return (
    <div className="content">
      <div className="content-header">
        <div>
          <Link to={`/domains/${user.domain}`} className="back-link">← Voltar para {user.domain}</Link>
          <h1>{user.username}</h1>
          <p className="domain-description">{user.name || 'Sem nome definido'}</p>
        </div>
        <div className="header-actions">
          <span className={`status ${user.active ? 'active' : 'inactive'}`} onClick={toggleActive}>
            {user.active ? 'Ativo' : 'Inativo'}
          </span>
          <button className="btn-primary" onClick={() => setShowPasswordModal(true)}>
            Alterar Senha
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <h3>{user.quotaUsed.mb} MB</h3>
          <p>Usado de {user.quotaMB} MB</p>
        </div>
        <div className="stat-card">
          <h3>{user.quotaUsed.percentUsed}%</h3>
          <p>Quota Utilizada</p>
        </div>
        <div className="stat-card">
          <h3>{user.quotaUsed.messages}</h3>
          <p>Mensagens</p>
        </div>
        <div className="stat-card">
          <h3>{user.isglobaladmin ? 'Global' : user.isadmin ? 'Sim' : 'Não'}</h3>
          <p>Administrador</p>
        </div>
      </div>

      {/* Quota Progress Bar */}
      <div className="quota-bar-container">
        <div className="quota-bar">
          <div
            className={`quota-bar-fill ${user.quotaUsed.percentUsed > 90 ? 'danger' : user.quotaUsed.percentUsed > 70 ? 'warning' : ''}`}
            style={{ width: `${Math.min(user.quotaUsed.percentUsed, 100)}%` }}
          ></div>
        </div>
        <span>{user.quotaUsed.percentUsed}% utilizado</span>
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button className={`tab ${activeTab === 'info' ? 'active' : ''}`} onClick={() => setActiveTab('info')}>
          Informações
        </button>
        <button className={`tab ${activeTab === 'inbox' ? 'active' : ''}`} onClick={() => setActiveTab('inbox')}>
          Caixa de Entrada ({inbox.length})
        </button>
        <button className={`tab ${activeTab === 'emails' ? 'active' : ''}`} onClick={() => setActiveTab('emails')}>
          Log de Transações
        </button>
        <button className={`tab ${activeTab === 'aliases' ? 'active' : ''}`} onClick={() => setActiveTab('aliases')}>
          Aliases
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'info' && (
        <div className="tab-content">
          <div className="info-section">
            <h3>Informações da Conta</h3>
            <div className="info-grid">
              <div><strong>Email:</strong> {user.username}</div>
              <div><strong>Domínio:</strong> {user.domain}</div>
              <div><strong>Nome:</strong> {user.name || '-'}</div>
              <div><strong>Criado em:</strong> {user.created}</div>
              <div><strong>Modificado em:</strong> {user.modified || '-'}</div>
              <div><strong>Senha alterada:</strong> {user.passwordlastchange || '-'}</div>
            </div>
          </div>

          <div className="info-section">
            <h3>Último Acesso</h3>
            <div className="info-grid">
              <div><strong>IMAP:</strong> {user.lastLogin.imap || 'Nunca'}</div>
              <div><strong>POP3:</strong> {user.lastLogin.pop3 || 'Nunca'}</div>
              <div><strong>Entrega Local:</strong> {user.lastLogin.lda || 'Nunca'}</div>
            </div>
          </div>

          <div className="info-section">
            <h3>Armazenamento</h3>
            <div className="info-grid">
              <div><strong>Diretório:</strong> {user.storagebasedirectory}/{user.storagenode}/{user.maildir}</div>
              <div><strong>Quota Total:</strong> {user.quotaMB} MB</div>
              <div><strong>Usado:</strong> {formatBytes(user.quotaUsed.bytes)}</div>
              <div><strong>Mensagens:</strong> {user.quotaUsed.messages}</div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'inbox' && (
        <div className="tab-content">
          <div className="inbox-controls">
            <form onSubmit={handleSearchInbox} className="search-form">
              <input
                type="text"
                placeholder="Buscar assunto ou remetente..."
                value={inboxSearch}
                onChange={(e) => setInboxSearch(e.target.value)}
              />
              <button type="submit" className="btn-primary">Buscar</button>
            </form>
          </div>

          {loadingInbox ? (
            <div className="loading">Lendo emails da caixa de entrada...</div>
          ) : inbox.length === 0 ? (
            <div className="empty-state">
              <p>Nenhum email encontrado.</p>
            </div>
          ) : (
            <>
              <div className="table-container">
                <table className="inbox-table">
                  <thead>
                    <tr>
                      <th className="col-date">Data</th>
                      <th className="col-from">De</th>
                      <th className="col-subject">Assunto</th>
                      <th className="col-action">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inbox.map((email, idx) => (
                      <tr key={idx} className="clickable-row" onClick={() => handleViewEmail(email.file)}>
                        <td className="col-date">
                          {email.dateIso ? new Date(email.dateIso).toLocaleString('pt-BR') : '-'}
                        </td>
                        <td className="col-from">
                          <div className="truncate" title={email.from}>{email.from}</div>
                        </td>
                        <td className="col-subject">
                          <div className="truncate" title={email.subject}><strong>{email.subject}</strong></div>
                        </td>
                        <td className="col-action">
                           <button className="btn-small" onClick={(e) => {
                             e.stopPropagation(); 
                             handleViewEmail(email.file);
                           }}>
                             Abrir
                           </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <div className="pagination-controls">
                <button 
                  disabled={inboxPage === 1} 
                  onClick={() => handlePageChange(inboxPage - 1)}
                >
                  Anterior
                </button>
                <span>Página {inboxPage} de {inboxTotalPages}</span>
                <button 
                  disabled={inboxPage === inboxTotalPages} 
                  onClick={() => handlePageChange(inboxPage + 1)}
                >
                  Próxima
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === 'emails' && (
        <div className="tab-content">
          {loadingEmails ? (
            <div className="loading">Carregando logs...</div>
          ) : emails.length === 0 ? (
            <div className="empty-state">
              <p>Nenhum registro de log recente encontrado.</p>
            </div>
          ) : (
            <div className="table-container">
              <p className="subtitle">Registro de conexões SMTP/LMTP (Logs)</p>
              <table>
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Direção</th>
                    <th>Endereço</th>
                    <th>Status</th>
                    <th>Tamanho</th>
                  </tr>
                </thead>
                <tbody>
                  {emails.map((email, idx) => (
                    <tr key={idx}>
                      <td>{email.date}</td>
                      <td>
                        <span className={`email-direction ${email.direction}`}>
                          {email.direction === 'enviado' ? '↑ Enviado' : '↓ Recebido'}
                        </span>
                      </td>
                      <td>{email.address}</td>
                      <td>
                        <span className={`email-status ${email.status}`}>
                          {email.status}
                        </span>
                      </td>
                      <td>{email.size ? formatBytes(email.size) : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'aliases' && (
        <div className="tab-content">
          <div className="info-section">
            <h3>Aliases que redirecionam para este email ({user.aliasesTo.length})</h3>
            {user.aliasesTo.length === 0 ? (
              <p className="empty-message">Nenhum alias configurado</p>
            ) : (
              <ul className="alias-list">
                {user.aliasesTo.map((a, idx) => (
                  <li key={idx}>{a.address} <small>(criado em {a.created})</small></li>
                ))}
              </ul>
            )}
          </div>

          <div className="info-section">
            <h3>Encaminhamentos deste email ({user.aliasesFrom.length})</h3>
            {user.aliasesFrom.length === 0 ? (
              <p className="empty-message">Nenhum encaminhamento configurado</p>
            ) : (
              <ul className="alias-list">
                {user.aliasesFrom.map((a, idx) => (
                  <li key={idx}>→ {a.forwarding} <small>(criado em {a.created})</small></li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Password Modal */}
      {showPasswordModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Alterar Senha</h2>
            <p>Usuário: <strong>{user.username}</strong></p>
            <form onSubmit={handleChangePassword}>
              <input
                type="password"
                placeholder="Nova senha (min. 6 caracteres)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
              />
              <div className="modal-actions">
                <button type="button" onClick={() => { setShowPasswordModal(false); setNewPassword(''); }}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">Alterar</button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}

// Server Config
function ServerConfig() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('config');

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await api.get('/server/config');
      setConfig(response.data);
    } catch (err) {
      console.error('Erro ao carregar configurações:', err);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copiado!');
  };

  if (loading) return <div className="loading">Carregando...</div>;
  if (!config) return <div className="content"><p>Erro ao carregar configurações</p></div>;

  return (
    <div className="content">
      <h1>Configurações do Servidor</h1>
      <p className="subtitle">Use estas configurações para configurar seu cliente de email</p>

      {/* Tabs */}
      <div className="tabs">
        <button className={`tab ${activeTab === 'config' ? 'active' : ''}`} onClick={() => setActiveTab('config')}>
          Configurações
        </button>
        <button className={`tab ${activeTab === 'instructions' ? 'active' : ''}`} onClick={() => setActiveTab('instructions')}>
          Instruções
        </button>
      </div>

      {activeTab === 'config' && (
        <div className="tab-content">
          <div className="config-cards">
            {/* IMAP */}
            <div className="config-card">
              <h3>IMAP (Recebimento)</h3>
              <div className="config-item">
                <label>Servidor:</label>
                <div className="config-value">
                  <span>{config.imap.server}</span>
                  <button className="btn-copy" onClick={() => copyToClipboard(config.imap.server)}>Copiar</button>
                </div>
              </div>
              <div className="config-item">
                <label>Porta (SSL):</label>
                <div className="config-value">
                  <span>{config.imap.port}</span>
                  <button className="btn-copy" onClick={() => copyToClipboard(config.imap.port.toString())}>Copiar</button>
                </div>
              </div>
              <div className="config-item">
                <label>Segurança:</label>
                <span className="security-badge ssl">{config.imap.security}</span>
              </div>
              <div className="config-alt">
                <small>Alternativa: Porta {config.imap.alternativePort} com {config.imap.alternativeSecurity}</small>
              </div>
            </div>

            {/* POP3 */}
            <div className="config-card">
              <h3>POP3 (Recebimento)</h3>
              <div className="config-item">
                <label>Servidor:</label>
                <div className="config-value">
                  <span>{config.pop3.server}</span>
                  <button className="btn-copy" onClick={() => copyToClipboard(config.pop3.server)}>Copiar</button>
                </div>
              </div>
              <div className="config-item">
                <label>Porta (SSL):</label>
                <div className="config-value">
                  <span>{config.pop3.port}</span>
                  <button className="btn-copy" onClick={() => copyToClipboard(config.pop3.port.toString())}>Copiar</button>
                </div>
              </div>
              <div className="config-item">
                <label>Segurança:</label>
                <span className="security-badge ssl">{config.pop3.security}</span>
              </div>
              <div className="config-alt">
                <small>Alternativa: Porta {config.pop3.alternativePort} com {config.pop3.alternativeSecurity}</small>
              </div>
            </div>

            {/* SMTP */}
            <div className="config-card">
              <h3>SMTP (Envio)</h3>
              <div className="config-item">
                <label>Servidor:</label>
                <div className="config-value">
                  <span>{config.smtp.server}</span>
                  <button className="btn-copy" onClick={() => copyToClipboard(config.smtp.server)}>Copiar</button>
                </div>
              </div>
              <div className="config-item">
                <label>Porta (SSL):</label>
                <div className="config-value">
                  <span>{config.smtp.port}</span>
                  <button className="btn-copy" onClick={() => copyToClipboard(config.smtp.port.toString())}>Copiar</button>
                </div>
              </div>
              <div className="config-item">
                <label>Segurança:</label>
                <span className="security-badge ssl">{config.smtp.security}</span>
              </div>
              <div className="config-alt">
                <small>Alternativa: Porta {config.smtp.alternativePort} com {config.smtp.alternativeSecurity}</small>
              </div>
            </div>
          </div>

          <div className="info-section">
            <h3>Informações Gerais</h3>
            <div className="info-grid">
              <div><strong>Usuário:</strong> Seu email completo (ex: usuario@dominio.com)</div>
              <div><strong>Senha:</strong> Sua senha do email</div>
              <div><strong>Webmail:</strong> <a href={config.webmail} target="_blank" rel="noopener noreferrer">{config.webmail}</a></div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'instructions' && (
        <div className="tab-content">
          <div className="instructions-grid">
            <div className="instruction-card">
              <h3>Microsoft Outlook</h3>
              <ol>
                {config.instructions.outlook.map((step, idx) => (
                  <li key={idx}>{step}</li>
                ))}
              </ol>
            </div>

            <div className="instruction-card">
              <h3>Mozilla Thunderbird</h3>
              <ol>
                {config.instructions.thunderbird.map((step, idx) => (
                  <li key={idx}>{step}</li>
                ))}
              </ol>
            </div>

            <div className="instruction-card">
              <h3>Celular (iOS/Android)</h3>
              <ol>
                {config.instructions.mobile.map((step, idx) => (
                  <li key={idx}>{step}</li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Aliases & Forwardings Manager
function Aliases() {
  const [activeTab, setActiveTab] = useState('alias'); // 'alias', 'forward', 'domain'
  const [data, setData] = useState([]);
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showSelf, setShowSelf] = useState(false); // Toggle para mostrar auto-envio
  
  // Form States
  const [formData, setFormData] = useState({ 
    address: '', forwarding: '', domain: '', target_domain: '' 
  });

  useEffect(() => {
    loadData();
  }, [activeTab, showSelf]);

  const loadData = async () => {
    setLoading(true);
    try {
      const domainsRes = await api.get('/domains');
      setDomains(domainsRes.data);

      let endpoint = '';
      if (activeTab === 'domain') {
        endpoint = '/alias-domains';
      } else {
        endpoint = `/aliases?type=${activeTab}&show_self=${showSelf}`;
      }
      
      const response = await api.get(endpoint);
      setData(response.data);
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      if (activeTab === 'domain') {
        await api.post('/alias-domains', {
          alias_domain: formData.address, // reusando campo address como alias domain
          target_domain: formData.target_domain
        });
      } else {
        await api.post('/aliases', {
          type: activeTab,
          address: formData.address,
          forwarding: formData.forwarding,
          domain: formData.domain
        });
      }
      setShowModal(false);
      setFormData({ address: '', forwarding: '', domain: '', target_domain: '' });
      loadData();
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao criar registro');
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Deseja realmente excluir este registro?`)) return;
    try {
      if (activeTab === 'domain') {
        await api.delete(`/alias-domains/${item.alias_domain}`);
      } else {
        // Para aliases e forwardings, enviamos address e forwarding para identificar único
        const params = new URLSearchParams();
        params.append('address', item.address);
        if (item.forwarding) params.append('forwarding', item.forwarding);
        
        await api.delete(`/aliases?${params.toString()}`);
      }
      loadData();
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao excluir registro');
    }
  };

  return (
    <div className="content">
      <div className="content-header">
        <h1>Gerenciamento de Aliases</h1>
        <div className="header-actions">
          {activeTab !== 'domain' && (
            <label className="checkbox-label">
              <input 
                type="checkbox" 
                checked={showSelf} 
                onChange={(e) => setShowSelf(e.target.checked)} 
              />
              Mostrar auto-envio (padrão)
            </label>
          )}
          <button className="btn-primary" onClick={() => setShowModal(true)}>
            + Novo
          </button>
        </div>
      </div>

      <div className="tabs">
        <button className={`tab ${activeTab === 'alias' ? 'active' : ''}`} onClick={() => setActiveTab('alias')}>
          Contas Alias
        </button>
        <button className={`tab ${activeTab === 'forward' ? 'active' : ''}`} onClick={() => setActiveTab('forward')}>
          Encaminhamentos
        </button>
        <button className={`tab ${activeTab === 'domain' ? 'active' : ''}`} onClick={() => setActiveTab('domain')}>
          Domínios Alias
        </button>
      </div>

      {loading ? (
        <div className="loading">Carregando...</div>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              {activeTab === 'domain' ? (
                <tr>
                  <th>Domínio Alias</th>
                  <th>Aponta para (Target)</th>
                  <th>Criado em</th>
                  <th>Ações</th>
                </tr>
              ) : (
                <tr>
                  <th>Endereço (Alias)</th>
                  <th>Encaminha Para</th>
                  <th>Domínio</th>
                  <th>Ações</th>
                </tr>
              )}
            </thead>
            <tbody>
              {data.map((item, idx) => (
                <tr key={idx}>
                  {activeTab === 'domain' ? (
                    <>
                      <td><strong>{item.alias_domain}</strong></td>
                      <td>{item.target_domain}</td>
                      <td>{item.created}</td>
                    </>
                  ) : (
                    <>
                      <td><strong>{item.address}</strong></td>
                      <td>{item.forwarding}</td>
                      <td>{item.domain}</td>
                    </>
                  )}
                  <td>
                    <button className="btn-danger btn-small" onClick={() => handleDelete(item)}>
                      Excluir
                    </button>
                  </td>
                </tr>
              ))}
              {data.length === 0 && (
                <tr><td colSpan="4" className="empty-message">Nenhum registro encontrado.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>
              {activeTab === 'alias' && 'Nova Conta Alias'}
              {activeTab === 'forward' && 'Novo Encaminhamento'}
              {activeTab === 'domain' && 'Novo Domínio Alias'}
            </h2>
            <form onSubmit={handleCreate}>
              
              {activeTab === 'domain' ? (
                <>
                  <label>Domínio Alias (ex: alias.com):</label>
                  <input
                    type="text"
                    placeholder="alias.com"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    required
                  />
                  <label>Domínio Alvo (ex: principal.com):</label>
                  <select
                    value={formData.target_domain}
                    onChange={(e) => setFormData({ ...formData, target_domain: e.target.value })}
                    required
                  >
                    <option value="">Selecione...</option>
                    {domains.map(d => (
                      <option key={d.domain} value={d.domain}>{d.domain}</option>
                    ))}
                  </select>
                </>
              ) : (
                <>
                  <label>Domínio:</label>
                  <select
                    value={formData.domain}
                    onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                    required
                  >
                    <option value="">Selecione...</option>
                    {domains.map(d => (
                      <option key={d.domain} value={d.domain}>{d.domain}</option>
                    ))}
                  </select>
                  
                  <label>Endereço Alias (apenas usuário):</label>
                  <input
                    type="text"
                    placeholder="Ex: contato"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    required
                  />
                  
                  <label>Encaminhar Para (email completo):</label>
                  <input
                    type="email"
                    placeholder="destino@gmail.com"
                    value={formData.forwarding}
                    onChange={(e) => setFormData({ ...formData, forwarding: e.target.value })}
                    required
                  />
                  
                  {activeTab === 'alias' && (
                    <p className="form-hint">
                      * Isso criará uma conta de alias virtual que não possui caixa postal, apenas encaminha.
                    </p>
                  )}
                </>
              )}

              <div className="modal-actions">
                <button type="button" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="btn-primary">Criar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function CatchAll() {
  const navigate = useNavigate();
  const userRole = localStorage.getItem('userRole');
  const [overview, setOverview] = useState([]);
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [scanLimit, setScanLimit] = useState(500);
  const [selected, setSelected] = useState(null); // { domain, target, address }
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalMessages, setTotalMessages] = useState(0);

  // Filtros da overview
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('last_seen'); // last_seen | count | address
  const [minCount, setMinCount] = useState(0);
  const [activeDomain, setActiveDomain] = useState('all'); // 'all' | domain

  // Filtros da tela de mensagens
  const [msgSearch, setMsgSearch] = useState('');

  // Domínios expandidos na overview (default: todos fechados)
  const [expandedDomains, setExpandedDomains] = useState(() => new Set());
  const toggleDomain = (d) => setExpandedDomains(prev => {
    const next = new Set(prev);
    if (next.has(d)) next.delete(d); else next.add(d);
    return next;
  });

  // Backfill state
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);

  useEffect(() => {
    loadOverview();
  }, [scanLimit]);

  const loadOverview = async () => {
    setLoadingOverview(true);
    try {
      const res = await api.get(`/catchall?scan=${scanLimit}`);
      setOverview(res.data.data || []);
    } catch (err) {
      console.error('Erro ao carregar catch-all:', err);
    } finally {
      setLoadingOverview(false);
    }
  };

  const openAddress = async (domain, target, address, p = 1) => {
    setSelected({ domain, target, address });
    setLoadingMessages(true);
    setPage(p);
    try {
      const res = await api.get(`/catchall/${encodeURIComponent(domain)}/messages`, {
        params: { address, page: p, limit: 20, scan: scanLimit * 2 },
      });
      setMessages(res.data.data || []);
      setTotalPages(res.data.meta.totalPages || 1);
      setTotalMessages(res.data.meta.total || 0);
    } catch (err) {
      console.error('Erro ao carregar mensagens:', err);
      setMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  };

  const openMessage = (file) => {
    if (!selected) return;
    navigate(`/email/${encodeURIComponent(selected.target)}/${encodeURIComponent(file)}`);
  };

  const formatDate = (ts) => {
    if (!ts) return '-';
    const d = new Date(ts);
    return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  const runBackfill = async () => {
    if (!window.confirm('Aplicar catch-all em todos os domínios ativos sem regra? Destino padrão: outros@viptecnologia.com.br')) return;
    setBackfilling(true);
    setBackfillResult(null);
    try {
      const res = await api.post('/catchall/backfill', {});
      setBackfillResult(res.data);
      await loadOverview();
    } catch (err) {
      setBackfillResult({ error: err.response?.data?.error || err.message });
    } finally {
      setBackfilling(false);
    }
  };

  // Aplica filtros à overview
  const filteredOverview = overview
    .filter(dom => activeDomain === 'all' || dom.domain === activeDomain)
    .map(dom => {
      let addrs = dom.addresses;
      if (search) {
        const s = search.toLowerCase();
        addrs = addrs.filter(a => a.address.includes(s));
      }
      if (minCount > 0) addrs = addrs.filter(a => a.count >= minCount);
      const sorted = [...addrs].sort((a, b) => {
        if (sortBy === 'count') return b.count - a.count;
        if (sortBy === 'address') return a.address.localeCompare(b.address);
        return b.last_seen - a.last_seen;
      });
      return { ...dom, addresses: sorted };
    });

  const filteredMessages = messages.filter(m => {
    if (!msgSearch) return true;
    const s = msgSearch.toLowerCase();
    return (m.subject || '').toLowerCase().includes(s) || (m.from || '').toLowerCase().includes(s);
  });

  if (selected) {
    return (
      <div className="content">
        <Link to="/catchall" onClick={(e) => { e.preventDefault(); setSelected(null); }} className="back-link">← Voltar para Catch-all</Link>
        <div className="content-header">
          <div>
            <h1><AtSign size={24} style={{ verticalAlign: 'middle', marginRight: 6 }} />{selected.address}</h1>
            <p className="subtitle">
              Recebido no catch-all de <strong>{selected.domain}</strong> · entregue em <code>{selected.target}</code>
            </p>
          </div>
          <span className="total-badge">{totalMessages.toLocaleString('pt-BR')} mensagens</span>
        </div>

        <div className="filters-bar">
          <div className="filters-row">
            <div className="filter-group filter-group-search">
              <label>Buscar nesta página</label>
              <div className="search-input-wrapper">
                <Search size={16} className="search-icon" />
                <input
                  type="text"
                  placeholder="Assunto ou remetente..."
                  value={msgSearch}
                  onChange={(e) => setMsgSearch(e.target.value)}
                />
                {msgSearch && (
                  <button type="button" className="search-clear-btn" onClick={() => setMsgSearch('')}>&#x2715;</button>
                )}
              </div>
            </div>
          </div>
        </div>

        {loadingMessages ? (
          <div className="loading">Carregando mensagens...</div>
        ) : messages.length === 0 ? (
          <div className="empty-message">
            <p><Inbox size={20} style={{ verticalAlign: 'middle', marginRight: 6 }} />Nenhuma mensagem encontrada para este endereço.</p>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>De</th>
                    <th>Assunto</th>
                    <th>Data</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMessages.map((m) => (
                    <tr key={m.file} className="clickable-row" onClick={() => openMessage(m.file)}>
                      <td>{m.from}</td>
                      <td>{m.subject}</td>
                      <td>{m.dateIso ? new Date(m.dateIso).toLocaleString('pt-BR') : m.date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="pagination-controls">
                <button disabled={page <= 1} onClick={() => openAddress(selected.domain, selected.target, selected.address, page - 1)}>&#8592; Anterior</button>
                <span>Página {page} de {totalPages}</span>
                <button disabled={page >= totalPages} onClick={() => openAddress(selected.domain, selected.target, selected.address, page + 1)}>Próxima &#8594;</button>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  return (
    <div className="content">
      <div className="content-header">
        <div>
          <h1><AtSign size={24} style={{ verticalAlign: 'middle', marginRight: 6 }} />Catch-all</h1>
          <p className="subtitle">Endereços virtuais extraídos do header <code>Received: for &lt;X&gt;</code> dos e-mails que caíram na caixa destino.</p>
        </div>
        <div className="header-actions">
          <button onClick={loadOverview} className="btn-secondary">
            <RefreshCw size={14} /> Atualizar
          </button>
          {userRole === 'superadmin' && (
            <button onClick={runBackfill} className="btn-primary" disabled={backfilling} title="Cria catch-all em todos os domínios ativos sem regra (destino padrão: outros@viptecnologia.com.br)">
              {backfilling ? 'Aplicando...' : 'Aplicar a todos os domínios'}
            </button>
          )}
        </div>
      </div>

      <div className="filters-bar">
        <div className="filters-row">
          <div className="filter-group filter-group-search">
            <label>Buscar endereço virtual</label>
            <div className="search-input-wrapper">
              <Search size={16} className="search-icon" />
              <input
                type="text"
                placeholder="Ex: financeiro, contato@..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button type="button" className="search-clear-btn" onClick={() => setSearch('')}>&#x2715;</button>
              )}
            </div>
          </div>
          <div className="filter-group">
            <label>Domínio</label>
            <select value={activeDomain} onChange={(e) => setActiveDomain(e.target.value)}>
              <option value="all">Todos ({overview.length})</option>
              {overview.map(d => (
                <option key={d.domain} value={d.domain}>{d.domain}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Ordenar</label>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="last_seen">Mais recente</option>
              <option value="count">Mais mensagens</option>
              <option value="address">Endereço (A-Z)</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Mínimo</label>
            <select value={minCount} onChange={(e) => setMinCount(parseInt(e.target.value))}>
              <option value={0}>Todos</option>
              <option value={2}>≥ 2 mensagens</option>
              <option value={5}>≥ 5 mensagens</option>
              <option value={10}>≥ 10 mensagens</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Janela</label>
            <select value={scanLimit} onChange={(e) => setScanLimit(parseInt(e.target.value))}>
              <option value={200}>Últimos 200</option>
              <option value={500}>Últimos 500</option>
              <option value={1000}>Últimos 1000</option>
              <option value={2000}>Últimos 2000</option>
            </select>
          </div>
        </div>
      </div>

      {backfillResult && (
        <div className="empty-message" style={{ borderLeft: `4px solid ${backfillResult.error ? '#d32f2f' : '#388e3c'}`, marginBottom: 16 }}>
          {backfillResult.error ? (
            <p><AlertCircle size={16} style={{ verticalAlign: 'middle', marginRight: 6, color: '#d32f2f' }} />Erro: {backfillResult.error}</p>
          ) : (
            <p>
              <CheckCircle size={16} style={{ verticalAlign: 'middle', marginRight: 6, color: '#388e3c' }} />
              Backfill OK — destino <code>{backfillResult.target}</code>:
              {' '}{backfillResult.created.length} criado(s){backfillResult.created.length > 0 ? ` (${backfillResult.created.join(', ')})` : ''}
              {backfillResult.skipped.length > 0 && `, ${backfillResult.skipped.length} já existia(m)`}.
            </p>
          )}
        </div>
      )}

      {loadingOverview ? (
        <div className="loading">Varredura em andamento...</div>
      ) : overview.length === 0 ? (
        <div className="empty-message">
          <p><AtSign size={20} style={{ verticalAlign: 'middle', marginRight: 6 }} />Nenhuma regra catch-all encontrada. Cadastre em Aliases (address igual ao domínio, sem @).</p>
        </div>
      ) : (
        filteredOverview.map((dom) => {
          const isOpen = expandedDomains.has(dom.domain);
          return (
            <div key={dom.domain} className={`catchall-section ${isOpen ? 'is-open' : ''}`}>
              <button
                type="button"
                className="catchall-section-header"
                aria-expanded={isOpen}
                onClick={() => toggleDomain(dom.domain)}
              >
                {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                <Globe size={16} className="catchall-section-icon" />
                <span className="catchall-section-title">{dom.domain}</span>
                <span className="catchall-section-count">{dom.addresses.length} endereços</span>
                <small className="catchall-section-meta">→ <code>{dom.target}</code> · {dom.scanned} arquivos</small>
              </button>
              {isOpen && (
                dom.addresses.length === 0 ? (
                  <p className="catchall-empty">Nenhum endereço virtual encontrado na janela varrida.</p>
                ) : (
                  <div className="table-container">
                    <table>
                      <thead>
                        <tr>
                          <th>Endereço (que recebeu)</th>
                          <th>Mensagens</th>
                          <th>Última mensagem</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {dom.addresses.map((a) => (
                          <tr key={a.address} className="clickable-row" onClick={() => openAddress(dom.domain, dom.target, a.address)}>
                            <td><AtSign size={14} style={{ verticalAlign: 'middle', marginRight: 4, opacity: 0.6 }} />{a.address}</td>
                            <td>{a.count}</td>
                            <td>{formatDate(a.last_seen)}</td>
                            <td><span className="btn-small">Ver mensagens →</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              )}
            </div>
          );
        })
      )}
    </div>
  );
}

// Messages - Monitor de Mensagens Global
function Messages() {
  const [messages, setMessages] = useState([]);
  const [domains, setDomains] = useState([]);
  const [mailboxes, setMailboxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalMessages, setTotalMessages] = useState(0);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterDomain, setFilterDomain] = useState('');
  const [filterMailbox, setFilterMailbox] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [committedSearch, setCommittedSearch] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    loadDomains();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 2);
    setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
    setEndDate(new Date().toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      loadMessages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filterDomain, filterMailbox, committedSearch, startDate, endDate]);

  const loadDomains = async () => {
    try {
      const response = await api.get('/domains');
      setDomains(response.data);
    } catch (err) {
      console.error('Erro ao carregar domínios:', err);
    }
  };

  const loadMessages = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (filterDomain) params.domain = filterDomain;
      if (filterMailbox) params.mailbox = filterMailbox;
      if (committedSearch) params.q = committedSearch;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const response = await api.get('/messages/recent', { params });
      setMessages(response.data.data);
      setTotalPages(response.data.meta.totalPages);
      setTotalMessages(response.data.meta.total);

      if (response.data.filters) {
        setMailboxes(response.data.filters.mailboxes || []);
      }
    } catch (err) {
      console.error('Erro ao carregar mensagens:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCommittedSearch(searchInput.trim());
    setPage(1);
  };

  const handleClearFilters = () => {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 2);
    setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
    setEndDate(new Date().toISOString().split('T')[0]);
    setFilterDomain('');
    setFilterMailbox('');
    setSearchInput('');
    setCommittedSearch('');
    setPage(1);
  };

  const handleDomainChange = (value) => {
    setFilterDomain(value);
    setFilterMailbox('');
    setPage(1);
  };

  const handleViewEmail = (msg) => {
    const folder = msg.folder && msg.folder !== 'INBOX' ? `?folder=${encodeURIComponent(msg.folder)}` : '';
    window.open(`/manager/email/${encodeURIComponent(msg.mailbox)}/${encodeURIComponent(msg.file)}${folder}`, '_blank');
  };

  const removeFilter = (filterKey) => {
    if (filterKey === 'domain') { setFilterDomain(''); setFilterMailbox(''); setPage(1); }
    else if (filterKey === 'mailbox') { setFilterMailbox(''); setPage(1); }
    else if (filterKey === 'search') { setSearchInput(''); setCommittedSearch(''); setPage(1); }
    else if (filterKey === 'startDate') {
      const d = new Date(); d.setDate(d.getDate() - 2);
      setStartDate(d.toISOString().split('T')[0]); setPage(1);
    } else if (filterKey === 'endDate') {
      setEndDate(new Date().toISOString().split('T')[0]); setPage(1);
    }
  };

  // Active filter pills
  const defaultStart = (() => { const d = new Date(); d.setDate(d.getDate() - 2); return d.toISOString().split('T')[0]; })();
  const defaultEnd = new Date().toISOString().split('T')[0];
  const activePills = [];
  if (startDate && startDate !== defaultStart) activePills.push({ key: 'startDate', label: `De: ${startDate}` });
  if (endDate && endDate !== defaultEnd) activePills.push({ key: 'endDate', label: `Até: ${endDate}` });
  if (filterDomain) activePills.push({ key: 'domain', label: `Domínio: ${filterDomain}` });
  if (filterMailbox) activePills.push({ key: 'mailbox', label: `Caixa: ${filterMailbox}` });
  if (committedSearch) activePills.push({ key: 'search', label: `Busca: "${committedSearch}"` });
  const hasNonDefaultFilters = activePills.length > 0;

  const filteredMailboxes = filterDomain
    ? mailboxes.filter(m => m.endsWith('@' + filterDomain))
    : mailboxes;

  const folderClass = (folder) => {
    if (!folder || folder === 'INBOX') return 'folder-badge folder-inbox';
    if (folder === 'Sent') return 'folder-badge folder-sent';
    if (folder === 'Junk' || folder === 'Spam') return 'folder-badge folder-junk';
    return 'folder-badge';
  };

  return (
    <div className="content messages-page">
      <div className="content-header">
        <div>
          <h1>Monitor de Mensagens</h1>
          <p className="subtitle">Acompanhe as mensagens de todas as caixas de entrada</p>
        </div>
        {totalMessages > 0 && (
          <span className="total-badge">{totalMessages.toLocaleString('pt-BR')} mensagens</span>
        )}
      </div>

      {/* Filter bar - all visible, no collapsible */}
      <div className="filters-bar">
        <div className="filters-row">
          <div className="filter-group">
            <label>Data inicial</label>
            <input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setPage(1); }} />
          </div>
          <div className="filter-group">
            <label>Data final</label>
            <input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setPage(1); }} />
          </div>
          <div className="filter-group">
            <label>Domínio</label>
            <select value={filterDomain} onChange={(e) => handleDomainChange(e.target.value)}>
              <option value="">Todos os domínios</option>
              {domains.map(d => <option key={d.domain} value={d.domain}>{d.domain}</option>)}
            </select>
          </div>
          <div className="filter-group">
            <label>Caixa de entrada</label>
            <select value={filterMailbox} onChange={(e) => { setFilterMailbox(e.target.value); setPage(1); }} disabled={filteredMailboxes.length === 0}>
              <option value="">Todas as caixas</option>
              {filteredMailboxes.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        </div>

        <form className="search-row" onSubmit={handleSearch}>
          <div className="filter-group filter-group-search">
            <label>Buscar por assunto ou remetente</label>
            <div className="search-input-wrapper">
              <Search size={16} className="search-icon" />
              <input
                type="text"
                placeholder="Assunto, remetente, email..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              {searchInput && (
                <button type="button" className="search-clear-btn" onClick={() => { setSearchInput(''); setCommittedSearch(''); setPage(1); }}>
                  &#x2715;
                </button>
              )}
            </div>
          </div>
          <div className="filter-group filter-group-actions">
            <label>&nbsp;</label>
            <div className="filter-actions-row">
              <button type="submit" className="btn-primary">
                <Search size={14} /> Buscar
              </button>
              <button type="button" className="btn-secondary" onClick={handleClearFilters} disabled={!hasNonDefaultFilters && !searchInput}>
                Limpar
              </button>
            </div>
          </div>
        </form>

        {activePills.length > 0 && (
          <div className="active-filters">
            <span className="active-filters-label">Filtros ativos:</span>
            {activePills.map(pill => (
              <span key={pill.key} className="filter-pill">
                {pill.label}
                <button type="button" className="filter-pill-remove" onClick={() => removeFilter(pill.key)}>&#x2715;</button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <div className="loading">Carregando mensagens...</div>
      ) : messages.length === 0 ? (
        <div className="empty-state">
          <Inbox size={48} />
          <p>Nenhuma mensagem encontrada.</p>
          {hasNonDefaultFilters && (
            <>
              <p className="empty-state-sub">Tente ajustar ou limpar os filtros aplicados.</p>
              <button className="btn-secondary" onClick={handleClearFilters}>Limpar filtros</button>
            </>
          )}
        </div>
      ) : (
        <>
          <div className="messages-table-container">
            <table className="messages-table">
              <thead>
                <tr>
                  <th className="col-date">Data/Hora</th>
                  <th className="col-mailbox">Caixa de Entrada</th>
                  <th className="col-folder">Pasta</th>
                  <th className="col-from">Remetente</th>
                  <th className="col-subject">Assunto</th>
                  <th className="col-action">Ações</th>
                </tr>
              </thead>
              <tbody>
                {messages.map((msg, idx) => (
                  <tr key={idx} className="clickable-row" onClick={() => handleViewEmail(msg)}>
                    <td className="col-date" data-label="Data">
                      <div className="date-cell">
                        <span className="date-main">{msg.mtime ? new Date(msg.mtime).toLocaleDateString('pt-BR') : (msg.dateIso ? new Date(msg.dateIso).toLocaleDateString('pt-BR') : '-')}</span>
                        <span className="date-time">{msg.mtime ? new Date(msg.mtime).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : (msg.dateIso ? new Date(msg.dateIso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '')}</span>
                      </div>
                    </td>
                    <td className="col-mailbox" data-label="Caixa">
                      <div className="mailbox-cell">
                        <span className="mailbox-user">{msg.mailbox?.split('@')[0]}</span>
                        <span className="mailbox-domain">@{msg.domain}</span>
                      </div>
                    </td>
                    <td className="col-folder" data-label="Pasta">
                      <span className={folderClass(msg.folder)}>{msg.folder || 'INBOX'}</span>
                    </td>
                    <td className="col-from" data-label="De">
                      <div className="truncate" title={msg.from}>{msg.from || '-'}</div>
                    </td>
                    <td className="col-subject" data-label="Assunto">
                      <div className="truncate" title={msg.subject}><strong>{msg.subject || '(Sem Assunto)'}</strong></div>
                    </td>
                    <td className="col-action" data-label="Ações">
                      <button className="btn-small" onClick={(e) => { e.stopPropagation(); handleViewEmail(msg); }}>Abrir</button>
                      <button className="btn-small" onClick={(e) => { e.stopPropagation(); navigate(`/users/${encodeURIComponent(msg.mailbox)}`); }}>Ver Conta</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pagination-controls">
            <button disabled={page === 1} onClick={() => setPage(page - 1)}>&#8592; Anterior</button>
            <span>Página <strong>{page}</strong> de <strong>{totalPages}</strong></span>
            <button disabled={page === totalPages} onClick={() => setPage(page + 1)}>Próxima &#8594;</button>
          </div>
        </>
      )}

    </div>
  );
}

// DNS Tools - Canivete Suíço de ferramentas DNS por domínio
function DnsTools() {
  const { domain } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');
  const [fixing, setFixing] = useState('');
  const [fixLogs, setFixLogs] = useState({});
  const [chatOpen, setChatOpen] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);

  const loadDns = async (silent = false) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      const response = await api.get(`/domains/${domain}/dns`);
      setData(response.data);
    } catch (err) {
      if (!silent) setError(err.response?.data?.error || 'Erro ao verificar DNS');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const [rechecking, setRechecking] = useState('');

  const recheckSingle = async (key) => {
    setRechecking(key);
    try {
      const response = await api.get(`/domains/${domain}/dns`);
      setData(response.data);
    } catch (e) {}
    setRechecking('');
  };

  useEffect(() => { loadDns(); }, [domain]);

  const copyText = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(''), 2000);
  };

  const handleFix = async (action) => {
    if (!window.confirm(`Deseja criar/corrigir o registro ${action.toUpperCase()} para ${domain} via Cloudflare?`)) return;
    setFixing(action);
    try {
      const response = await api.post(`/domains/${domain}/dns/fix`, { action });
      setFixLogs(prev => ({ ...prev, [action]: { ...response.data, timestamp: new Date().toLocaleTimeString('pt-BR') } }));
    } catch (err) {
      setFixLogs(prev => ({ ...prev, [action]: { success: false, response: err.response?.data?.error || 'Erro ao corrigir', timestamp: new Date().toLocaleTimeString('pt-BR') } }));
    } finally {
      setFixing('');
    }
  };

  const handleChat = async (e) => {
    e.preventDefault();
    if (!chatInput.trim()) return;
    const userMsg = chatInput.trim();
    setChatInput('');
    setChatLoading(true);

    const newHistory = [...chatHistory, { role: 'user', content: userMsg }];
    setChatHistory(newHistory);

    try {
      const response = await api.post(`/domains/${domain}/dns/chat`, {
        message: userMsg,
        history: chatHistory,
      });
      setChatHistory([...newHistory, response.data.message]);
    } catch (err) {
      setChatHistory([...newHistory, { role: 'assistant', content: 'Erro: ' + (err.response?.data?.error || err.message) }]);
    } finally {
      setChatLoading(false);
    }
  };

  const renderMarkdown = (text) => {
    if (!text) return '';
    const lines = text.split('\n');
    const out = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i];

      // Table: detect line starting with |
      if (line.trim().startsWith('|') && line.trim().endsWith('|')) {
        const tableLines = [];
        while (i < lines.length && lines[i].trim().startsWith('|') && lines[i].trim().endsWith('|')) {
          tableLines.push(lines[i]);
          i++;
        }
        // Parse table: first line = header, second = separator (skip), rest = body
        if (tableLines.length >= 2) {
          const parseRow = (row) => row.split('|').slice(1, -1).map(c => c.trim());
          const headers = parseRow(tableLines[0]);
          const thHtml = headers.map(h => `<th>${inlineFormat(h)}</th>`).join('');
          let bodyHtml = '';
          for (let r = 2; r < tableLines.length; r++) {
            const cells = parseRow(tableLines[r]);
            bodyHtml += '<tr>' + cells.map(c => `<td>${inlineFormat(c)}</td>`).join('') + '</tr>';
          }
          out.push(`<table class="dns-chat-table"><thead><tr>${thHtml}</tr></thead><tbody>${bodyHtml}</tbody></table>`);
        }
        continue;
      }

      // Headers
      if (line.startsWith('### ')) { out.push(`<h4>${inlineFormat(line.slice(4))}</h4>`); i++; continue; }
      if (line.startsWith('## ')) { out.push(`<h3>${inlineFormat(line.slice(3))}</h3>`); i++; continue; }
      if (line.startsWith('# ')) { out.push(`<h2>${inlineFormat(line.slice(2))}</h2>`); i++; continue; }

      // List items
      if (line.startsWith('- ')) {
        const items = [];
        while (i < lines.length && lines[i].startsWith('- ')) {
          items.push(`<li>${inlineFormat(lines[i].slice(2))}</li>`);
          i++;
        }
        out.push(`<ul>${items.join('')}</ul>`);
        continue;
      }

      // Empty line
      if (line.trim() === '') { out.push('<br>'); i++; continue; }

      // Normal paragraph
      out.push(`<p>${inlineFormat(line)}</p>`);
      i++;
    }

    return out.join('');
  };

  const inlineFormat = (text) => {
    return text
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/`(.+?)`/g, '<code>$1</code>');
  };

  const StatusIcon = ({ status }) => {
    if (status === 'ok') return <CheckCircle size={20} className="dns-icon-ok" />;
    if (status === 'warning') return <AlertCircle size={20} className="dns-icon-warning" />;
    return <XCircle size={20} className="dns-icon-missing" />;
  };

  const StatusLabel = ({ status }) => {
    if (status === 'ok') return <span className="badge badge-approved">Configurado</span>;
    if (status === 'warning') return <span className="badge badge-pending">Atenção</span>;
    return <span className="badge badge-frozen">Ausente</span>;
  };

  const RecheckBtn = ({ checkKey }) => (
    <button
      className="btn-ghost btn-small"
      onClick={() => recheckSingle(checkKey)}
      disabled={rechecking === checkKey}
      title="Re-verificar este registro"
    >
      <RefreshCw size={13} className={rechecking === checkKey ? 'spin' : ''} />
    </button>
  );

  const FixButton = ({ action }) => (
    <div className="dns-fix-wrapper">
      <button
        className="btn-primary btn-small"
        onClick={() => handleFix(action)}
        disabled={fixing === action}
      >
        {fixing === action ? 'Criando...' : `Criar via Cloudflare`}
      </button>
      {fixLogs[action] && (
        <div className={`dns-fix-result ${fixLogs[action].success ? 'dns-fix-success' : 'dns-fix-error'}`}>
          <div className="dns-fix-result-header">
            {fixLogs[action].success ? <CheckCircle size={14} /> : <AlertTriangle size={14} />}
            <span>{fixLogs[action].success ? 'Sucesso' : 'Atenção'} — {fixLogs[action].timestamp}</span>
          </div>
          <div className="dns-fix-result-body dns-chat-md" dangerouslySetInnerHTML={{ __html: renderMarkdown(fixLogs[action].response) }} />
          {fixLogs[action].success && <p className="form-hint">DNS será re-verificado em 5s...</p>}
        </div>
      )}
    </div>
  );

  if (loading) return <div className="loading">Verificando registros DNS...</div>;
  if (error) return <div className="content"><div className="error">{error}</div></div>;
  if (!data) return null;

  const { checks, hostname, serverIp } = data;

  return (
    <div className="content">
      <div className="content-header">
        <div>
          <Link to={`/domains/${domain}`} className="back-link">← Voltar para {domain}</Link>
          <h1>Ferramentas DNS</h1>
          <p className="domain-description">{domain} — Servidor: {hostname} ({serverIp})</p>
        </div>
        <button className="btn-secondary" onClick={loadDns}>
          <RefreshCw size={16} /> Verificar novamente
        </button>
      </div>

      {/* Summary */}
      <div className="dns-summary">
        {[
          { key: 'mx', label: 'MX' },
          { key: 'spf', label: 'SPF' },
          { key: 'dkim', label: 'DKIM' },
          { key: 'dmarc', label: 'DMARC' },
          { key: 'ptr', label: 'PTR' },
          { key: 'aRecord', label: 'A' },
        ].map(item => (
          <div key={item.key} className={`dns-summary-item dns-summary-${checks[item.key].status}`}>
            <StatusIcon status={checks[item.key].status} />
            <span>{item.label}</span>
          </div>
        ))}
      </div>

      {/* MX Record */}
      <div className="dns-card">
        <div className="dns-card-header">
          <div className="dns-card-title">
            <StatusIcon status={checks.mx.status} />
            <div>
              <h3>Registro MX (Mail Exchanger)</h3>
              <p>Define qual servidor recebe os e-mails do domínio</p>
            </div>
          </div>
          <div className="dns-badge-group"><StatusLabel status={checks.mx.status} /><RecheckBtn checkKey="mx" /></div>
        </div>
        <div className="dns-card-body">
          {checks.mx.records.length > 0 ? (
            <div className="dns-records">
              <div className="dns-record-label">Registros encontrados:</div>
              {checks.mx.records.map((r, i) => (
                <div key={i} className="dns-record-value">
                  <code>Prioridade {r.priority} → {r.exchange}</code>
                  {!checks.mx.pointsToMailServer && <span className="dns-hint-warn">Não aponta para {hostname}</span>}
                </div>
              ))}
            </div>
          ) : (
            <div className="dns-expected">
              <div className="dns-record-label">Registro esperado:</div>
              <div className="dns-copyable">
                <code>{checks.mx.expected}</code>
                <button className="btn-copy" onClick={() => copyText(checks.mx.expected, 'mx')}>
                  {copied === 'mx' ? 'Copiado!' : 'Copiar'}
                </button>
                <FixButton action="mx" />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* A Record */}
      <div className="dns-card">
        <div className="dns-card-header">
          <div className="dns-card-title">
            <StatusIcon status={checks.aRecord.status} />
            <div>
              <h3>Registro A</h3>
              <p>Aponta mail.{domain} para o IP do servidor</p>
            </div>
          </div>
          <div className="dns-badge-group"><StatusLabel status={checks.aRecord.status} /><RecheckBtn checkKey="aRecord" /></div>
        </div>
        <div className="dns-card-body">
          {checks.aRecord.records.length > 0 ? (
            <div className="dns-records">
              <div className="dns-record-label">IPs encontrados:</div>
              {checks.aRecord.records.map((ip, i) => (
                <div key={i} className="dns-record-value">
                  <code>{ip}</code>
                  {ip === serverIp ? <span className="dns-hint-ok">IP do servidor</span> : <span className="dns-hint-warn">IP diferente do servidor ({serverIp})</span>}
                </div>
              ))}
            </div>
          ) : (
            <div className="dns-expected">
              <div className="dns-record-label">Registro esperado:</div>
              <div className="dns-copyable">
                <code>{checks.aRecord.expected}</code>
                <button className="btn-copy" onClick={() => copyText(checks.aRecord.expected, 'a')}>
                  {copied === 'a' ? 'Copiado!' : 'Copiar'}
                </button>
                <FixButton action="a" />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* PTR Record */}
      <div className="dns-card">
        <div className="dns-card-header">
          <div className="dns-card-title">
            <StatusIcon status={checks.ptr.status} />
            <div>
              <h3>Registro PTR (Reverso)</h3>
              <p>Resolve o IP para o hostname — essencial para evitar bloqueio por spam</p>
            </div>
          </div>
          <div className="dns-badge-group"><StatusLabel status={checks.ptr.status} /><RecheckBtn checkKey="ptr" /></div>
        </div>
        <div className="dns-card-body">
          {checks.ptr.records.length > 0 ? (
            <div className="dns-records">
              <div className="dns-record-label">Reverso atual:</div>
              {checks.ptr.records.map((r, i) => (
                <div key={i} className="dns-record-value">
                  <code>{serverIp} → {r}</code>
                  {checks.ptr.matchesHostname ? <span className="dns-hint-ok">Correto</span> : <span className="dns-hint-warn">Deveria apontar para {hostname}</span>}
                </div>
              ))}
            </div>
          ) : (
            <div className="dns-expected">
              <div className="dns-record-label">Esperado (configurar com seu provedor/ISP):</div>
              <div className="dns-copyable">
                <code>{checks.ptr.expected}</code>
              </div>
              <p className="form-hint">O registro PTR é gerenciado pelo provedor de hospedagem/ISP. Entre em contato com eles para configurar.</p>
            </div>
          )}
        </div>
      </div>

      {/* SPF Record */}
      <div className="dns-card">
        <div className="dns-card-header">
          <div className="dns-card-title">
            <StatusIcon status={checks.spf.status} />
            <div>
              <h3>Registro SPF</h3>
              <p>Define quais servidores podem enviar e-mails pelo domínio</p>
            </div>
          </div>
          <div className="dns-badge-group"><StatusLabel status={checks.spf.status} /><RecheckBtn checkKey="spf" /></div>
        </div>
        <div className="dns-card-body">
          {checks.spf.records.length > 0 ? (
            <div className="dns-records">
              <div className="dns-record-label">Registro atual:</div>
              {checks.spf.records.map((r, i) => (
                <div key={i} className="dns-record-value">
                  <code>{r}</code>
                  <div className="dns-copyable">
                    <button className="btn-copy" onClick={() => copyText(r, `spf${i}`)}>
                      {copied === `spf${i}` ? 'Copiado!' : 'Copiar'}
                    </button>
                  </div>
                </div>
              ))}
              {checks.spf.status === 'warning' && (
                <p className="form-hint">Recomendado usar <code>-all</code> (hard fail) ou <code>~all</code> (soft fail) ao final do registro SPF.</p>
              )}
            </div>
          ) : (
            <div className="dns-expected">
              <div className="dns-record-label">Registro TXT esperado para {domain}:</div>
              <div className="dns-copyable">
                <code>{checks.spf.expected}</code>
                <button className="btn-copy" onClick={() => copyText(checks.spf.expected, 'spf')}>
                  {copied === 'spf' ? 'Copiado!' : 'Copiar'}
                </button>
                <FixButton action="spf" />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* DKIM Record */}
      <div className="dns-card">
        <div className="dns-card-header">
          <div className="dns-card-title">
            <StatusIcon status={checks.dkim.status} />
            <div>
              <h3>Registro DKIM</h3>
              <p>Assinatura digital que valida a autenticidade dos e-mails enviados</p>
            </div>
          </div>
          <div className="dns-badge-group"><StatusLabel status={checks.dkim.status} /><RecheckBtn checkKey="dkim" /></div>
        </div>
        <div className="dns-card-body">
          {checks.dkim.records.length > 0 ? (
            <div className="dns-records">
              <div className="dns-record-label">Registro em <code>{checks.dkim.recordName}</code>:</div>
              {checks.dkim.records.map((r, i) => (
                <div key={i} className="dns-record-value dns-record-wrap">
                  <code>{r}</code>
                  <div className="dns-copyable">
                    <button className="btn-copy" onClick={() => copyText(r, `dkim${i}`)}>
                      {copied === `dkim${i}` ? 'Copiado!' : 'Copiar'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="dns-expected">
              <div className="dns-record-label">Registro TXT esperado em <code>{checks.dkim.recordName}</code>:</div>
              {checks.dkim.serverKey ? (
                <div className="dns-copyable">
                  <code className="dns-record-wrap">{checks.dkim.serverKey}</code>
                  <button className="btn-copy" onClick={() => copyText(checks.dkim.serverKey, 'dkim-key')}>
                    {copied === 'dkim-key' ? 'Copiado!' : 'Copiar'}
                  </button>
                  <FixButton action="dkim" />
                </div>
              ) : (
                <div>
                  <p className="form-hint">Chave DKIM não encontrada no servidor para este domínio.</p>
                  <FixButton action="dkim" />
                  <p className="form-hint">Gera a chave no servidor, configura o Amavisd, e cria o registro no Cloudflare automaticamente.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* DMARC Record */}
      <div className="dns-card">
        <div className="dns-card-header">
          <div className="dns-card-title">
            <StatusIcon status={checks.dmarc.status} />
            <div>
              <h3>Registro DMARC</h3>
              <p>Política que combina SPF e DKIM para proteger contra phishing</p>
            </div>
          </div>
          <div className="dns-badge-group"><StatusLabel status={checks.dmarc.status} /><RecheckBtn checkKey="dmarc" /></div>
        </div>
        <div className="dns-card-body">
          {checks.dmarc.records.length > 0 ? (
            <div className="dns-records">
              <div className="dns-record-label">Registro em <code>_dmarc.{domain}</code>:</div>
              {checks.dmarc.records.map((r, i) => (
                <div key={i} className="dns-record-value dns-record-wrap">
                  <code>{r}</code>
                  <div className="dns-copyable">
                    <button className="btn-copy" onClick={() => copyText(r, `dmarc${i}`)}>
                      {copied === `dmarc${i}` ? 'Copiado!' : 'Copiar'}
                    </button>
                  </div>
                </div>
              ))}
              {checks.dmarc.policy === 'none' && (
                <p className="form-hint">Política atual: <code>p=none</code> (apenas monitoramento). Recomendado migrar para <code>p=quarantine</code> ou <code>p=reject</code>.</p>
              )}
            </div>
          ) : (
            <div className="dns-expected">
              <div className="dns-record-label">Registro TXT esperado em <code>_dmarc.{domain}</code>:</div>
              <div className="dns-copyable">
                <code>{checks.dmarc.expected}</code>
                <button className="btn-copy" onClick={() => copyText(checks.dmarc.expected, 'dmarc')}>
                  {copied === 'dmarc' ? 'Copiado!' : 'Copiar'}
                </button>
                <FixButton action="dmarc" />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Autodiscover / Autoconfig */}
      <div className="dns-card">
        <div className="dns-card-header">
          <div className="dns-card-title">
            <Info size={20} className="dns-icon-info" />
            <div>
              <h3>Autodiscover / Autoconfig</h3>
              <p>Permite configuração automática de clientes de e-mail (Outlook, Thunderbird)</p>
            </div>
          </div>
          <div className="dns-badge-group">
            <StatusLabel status={checks.autodiscover.status} />
            <StatusLabel status={checks.autoconfig.status} />
            <RecheckBtn checkKey="autodiscover" />
          </div>
        </div>
        <div className="dns-card-body">
          <div className="dns-records">
            <div className="dns-record-value">
              <code>autodiscover.{domain}</code>
              {checks.autodiscover.status === 'ok'
                ? <span className="dns-hint-ok">Configurado</span>
                : <span className="dns-hint-warn">Ausente</span>
              }
            </div>
            <div className="dns-record-value">
              <code>autoconfig.{domain}</code>
              {checks.autoconfig.status === 'ok'
                ? <span className="dns-hint-ok">Configurado</span>
                : <span className="dns-hint-warn">Ausente</span>
              }
            </div>
          </div>
          {(checks.autodiscover.status !== 'ok' || checks.autoconfig.status !== 'ok') && (
            <div className="dns-expected">
              <div className="dns-record-label">Registros A ou CNAME esperados:</div>
              <div className="dns-copyable">
                <code>autodiscover.{domain}. IN A {serverIp}</code>
                <button className="btn-copy" onClick={() => copyText(`autodiscover.${domain}. IN A ${serverIp}`, 'autodisc')}>
                  {copied === 'autodisc' ? 'Copiado!' : 'Copiar'}
                </button>
                {checks.autodiscover.status !== 'ok' && <FixButton action="autodiscover" />}
              </div>
              <div className="dns-copyable">
                <code>autoconfig.{domain}. IN A {serverIp}</code>
                <button className="btn-copy" onClick={() => copyText(`autoconfig.${domain}. IN A ${serverIp}`, 'autoconf')}>
                  {copied === 'autoconf' ? 'Copiado!' : 'Copiar'}
                </button>
                {checks.autoconfig.status !== 'ok' && <FixButton action="autoconfig" />}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Useful Links */}
      <div className="dns-card">
        <div className="dns-card-header">
          <div className="dns-card-title">
            <ExternalLink size={20} className="dns-icon-info" />
            <div>
              <h3>Links Úteis</h3>
              <p>Ferramentas externas para diagnóstico de e-mail</p>
            </div>
          </div>
        </div>
        <div className="dns-card-body">
          <div className="dns-links">
            <a href={`https://mxtoolbox.com/SuperTool.aspx?action=mx%3a${domain}&run=toolpage`} target="_blank" rel="noopener noreferrer" className="dns-link">
              <Globe size={14} /> MXToolbox — Verificar MX
            </a>
            <a href={`https://www.mail-tester.com/`} target="_blank" rel="noopener noreferrer" className="dns-link">
              <Mail size={14} /> Mail-Tester — Testar score de e-mail
            </a>
            <a href={`https://postmaster.google.com/`} target="_blank" rel="noopener noreferrer" className="dns-link">
              <Globe size={14} /> Google Postmaster Tools
            </a>
            <a href={`https://mxtoolbox.com/SuperTool.aspx?action=dkim%3a${domain}%3adkim&run=toolpage`} target="_blank" rel="noopener noreferrer" className="dns-link">
              <Shield size={14} /> MXToolbox — Verificar DKIM
            </a>
            <a href={`https://mxtoolbox.com/SuperTool.aspx?action=spf%3a${domain}&run=toolpage`} target="_blank" rel="noopener noreferrer" className="dns-link">
              <ShieldCheck size={14} /> MXToolbox — Verificar SPF
            </a>
            <a href={`https://mxtoolbox.com/SuperTool.aspx?action=dmarc%3a${domain}&run=toolpage`} target="_blank" rel="noopener noreferrer" className="dns-link">
              <ShieldCheck size={14} /> MXToolbox — Verificar DMARC
            </a>
          </div>
        </div>
      </div>

      {/* Chat with Cloudflare Agent */}
      <div className="dns-card">
        <div className="dns-card-header dns-chat-header" onClick={() => setChatOpen(!chatOpen)}>
          <div className="dns-card-title">
            <MessageSquare size={20} className="dns-icon-info" />
            <div>
              <h3>Chat DNS (Cloudflare Agent)</h3>
              <p>Converse com a IA para gerenciar registros DNS deste domínio</p>
            </div>
          </div>
          <span className="badge badge-info">{chatOpen ? 'Fechar' : 'Abrir'}</span>
        </div>
        {chatOpen && (
          <div className="dns-card-body dns-chat-body">
            <div className="dns-chat-messages">
              {chatHistory.length === 0 && (
                <div className="dns-chat-empty">
                  <p>Pergunte qualquer coisa sobre o DNS de <strong>{domain}</strong>.</p>
                  <div className="dns-chat-suggestions">
                    {[
                      `Mostre os registros de ${domain}`,
                      `Verifique a propagação de ${domain}`,
                      `Liste todos os domínios`,
                    ].map((s, i) => (
                      <button key={i} className="btn-small" onClick={() => { setChatInput(s); }}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {chatHistory.map((msg, i) => (
                <div key={i} className={`dns-chat-msg dns-chat-${msg.role}`}>
                  <div className="dns-chat-msg-label">{msg.role === 'user' ? 'Você' : 'Agente DNS'}</div>
                  {msg.role === 'user' ? (
                    <pre className="dns-chat-msg-content">{msg.content}</pre>
                  ) : (
                    <div className="dns-chat-msg-content dns-chat-md" dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }} />
                  )}
                </div>
              ))}
              {chatLoading && <div className="loading">Consultando agente DNS...</div>}
            </div>
            <form className="dns-chat-input" onSubmit={handleChat}>
              <input
                type="text"
                placeholder={`Ex: Crie um registro TXT para ${domain}...`}
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                disabled={chatLoading}
              />
              <button type="submit" className="btn-primary" disabled={chatLoading || !chatInput.trim()}>
                Enviar
              </button>
            </form>
          </div>
        )}
      </div>

      <p className="form-hint">Verificado em {new Date(data.checkedAt).toLocaleString('pt-BR')}</p>
    </div>
  );
}

// Email Viewer - Standalone page (opens in new tab, no sidebar)
function EmailViewer() {
  const { mailbox, filename } = useParams();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const folder = searchParams.get('folder');

  useEffect(() => {
    loadEmail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mailbox, filename]);

  const loadEmail = async () => {
    try {
      const params = folder && folder !== 'INBOX' ? { folder } : {};
      const response = await api.get(`/users/${encodeURIComponent(mailbox)}/inbox/${encodeURIComponent(filename)}`, { params });
      setEmail(response.data);
    } catch (err) {
      console.error('Erro ao carregar email:', err);
      setError('Erro ao carregar o email. Verifique se tem permissão de acesso.');
    } finally {
      setLoading(false);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const downloadAttachment = async (att, idx) => {
    try {
      const params = folder && folder !== 'INBOX' ? { folder } : {};
      const response = await api.get(
        `/users/${encodeURIComponent(mailbox)}/inbox/${encodeURIComponent(filename)}/attachments/${idx}`,
        { params, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: att.contentType || 'application/octet-stream' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = att.filename || `anexo-${idx}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erro ao baixar anexo:', err);
      alert('Não foi possível baixar o anexo.');
    }
  };

  if (loading) return (
    <div className="email-viewer-page">
      <div className="loading">Carregando email...</div>
    </div>
  );

  if (error) return (
    <div className="email-viewer-page">
      <div className="email-viewer-error">
        <AlertTriangle size={48} />
        <p>{error}</p>
      </div>
    </div>
  );

  if (!email) return null;

  return (
    <div className="email-viewer-page">
      <div className="email-viewer-toolbar">
        <div className="email-viewer-mailbox">
          <Mail size={16} />
          <span>{decodeURIComponent(mailbox)}</span>
        </div>
        <button className="btn-secondary btn-small" onClick={() => window.close()}>
          Fechar aba
        </button>
      </div>
      <div className="email-viewer-content">
        <div className="email-viewer-header">
          <h1>{email.subject || '(Sem Assunto)'}</h1>
        </div>
        <div className="email-viewer-meta">
          <div className="meta-row">
            <span className="label">De:</span>
            <span className="value">{email.from?.map(f => f.name ? `${f.name} <${f.address}>` : f.address).join(', ')}</span>
          </div>
          <div className="meta-row">
            <span className="label">Para:</span>
            <span className="value">{email.to?.map(t => t.name ? `${t.name} <${t.address}>` : t.address).join(', ')}</span>
          </div>
          <div className="meta-row">
            <span className="label">Data:</span>
            <span className="value">{email.date ? new Date(email.date).toLocaleString('pt-BR') : '-'}</span>
          </div>
          {email.attachments && email.attachments.length > 0 && (
            <div className="meta-row">
              <span className="label">Anexos:</span>
              <div className="value attachments-list">
                {email.attachments.map((att, idx) => (
                  <button
                    key={idx}
                    type="button"
                    className="attachment-chip"
                    onClick={() => downloadAttachment(att, idx)}
                    title={`Baixar ${att.filename || `anexo-${idx}`}`}
                  >
                    <Paperclip size={12} />
                    <span className="attachment-name">{att.filename || `anexo-${idx}`}</span>
                    <span className="attachment-size">({formatBytes(att.size || 0)})</span>
                    <Download size={12} />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="email-viewer-body">
          {email.html ? (
            <iframe title="email-content" srcDoc={email.html} sandbox="allow-same-origin" />
          ) : (
            <pre>{email.text}</pre>
          )}
        </div>
      </div>
    </div>
  );
}

// App Layout (with sidebar)
function AppLayout({ onLogout }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="app-container">
      <button className="sidebar-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
        {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
      </button>
      <div className={`sidebar-overlay ${sidebarOpen ? 'visible' : ''}`} onClick={() => setSidebarOpen(false)} />
      <Sidebar onLogout={onLogout} isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <main>
        <Outlet />
      </main>
    </div>
  );
}

// App Principal
function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('token'));

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <Router basename="/manager">
        {!isAuthenticated ? (
          <Login onLogin={() => setIsAuthenticated(true)} />
        ) : (
          <Routes>
            <Route path="/email/:mailbox/:filename" element={<EmailViewer />} />
            <Route element={<AppLayout onLogout={() => setIsAuthenticated(false)} />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/messages" element={<Messages />} />
              <Route path="/domains" element={<Domains />} />
              <Route path="/domains/:domain" element={<DomainDetails />} />
              <Route path="/domains/:domain/dns" element={<DnsTools />} />
              <Route path="/users" element={<Users />} />
              <Route path="/users/:username" element={<UserDetails />} />
              <Route path="/aliases" element={<Aliases />} />
              <Route path="/config" element={<ServerConfig />} />
              <Route path="/access" element={<AccessManagement />} />
              <Route path="/catchall" element={<CatchAll />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Route>
          </Routes>
        )}
      </Router>
    </GoogleOAuthProvider>
  );
}

export default App;
